import { forwardRef, useImperativeHandle } from 'react';

import { Box, Button, Flex, Grid, Icon, Text, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuPlus, LuTrash2 } from 'react-icons/lu';
import { z } from 'zod';

import { Form, FormField } from '@/components';

import { useAccountData } from './AccountDataContext';

// Updated schema to align with API ForeignLanguageRequest structure
const languageItemSchema = z.object({
  language: z
    .string()
    .min(1, t('common:account.validation.foreignLanguageRequired')),
  level: z.string().min(1, t('common:account.validation.levelRequired')),
});

const skillInfoSchema = z.object({
  languages: z.array(languageItemSchema),
});

export type SkillInfoFormFields = z.infer<typeof skillInfoSchema>;
export type LanguageItemFormFields = z.infer<typeof languageItemSchema>;

export interface SkillInfoFormRef {
  submit: () => void;
}

export const SkillInfoForm = forwardRef<SkillInfoFormRef>((_props, ref) => {
  const { t } = useTranslation(['common', 'candidate', 'cv', 'components']);
  const { data, updateForeignLanguage } = useAccountData();

  const languageLevels = [
    { value: 'beginner', label: t('common:account.levels.beginner') },
    { value: 'elementary', label: t('common:account.levels.elementary') },
    { value: 'intermediate', label: t('common:account.levels.intermediate') },
    {
      value: 'upper-intermediate',
      label: t('common:account.levels.upperIntermediate'),
    },
    { value: 'advanced', label: t('common:account.levels.advanced') },
    { value: 'native', label: t('common:account.levels.native') },
  ];

  // Create proper default language item
  const createDefaultLanguage = (): LanguageItemFormFields => ({
    language: '',
    level: '',
  });

  // Transform API data to form format
  const getFormDefaultValues = (): SkillInfoFormFields => {
    if (!data?.foreignLanguage?.length) {
      return {
        languages: [createDefaultLanguage()],
      };
    }

    const transformedLanguages = data.foreignLanguage.map((lang) => ({
      language: lang.language ?? '',
      level: lang.level ?? '',
    }));

    return { languages: transformedLanguages };
  };

  const form = useForm<SkillInfoFormFields>({
    mode: 'onBlur',
    resolver: zodResolver(skillInfoSchema),
    values: getFormDefaultValues(),
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'languages',
  });

  // Expose submit function to parent via ref
  useImperativeHandle(ref, () => ({
    submit: () => {
      form.handleSubmit(handleSubmit)();
    },
  }));

  const handleSubmit = async (formData: SkillInfoFormFields) => {
    // Use form data directly as it matches API format
    await updateForeignLanguage(formData.languages);
  };

  return (
    <Form {...form} onSubmit={handleSubmit}>
      <VStack spacing={6} align="stretch">
        <Flex justify="space-between" align="center" mb={2}>
          <Text fontWeight="bold" fontSize={{ base: 18, md: 20 }}>
            {t('common:account.fields.foreignLanguage.label')}
          </Text>
          <Button
            onClick={() => append(createDefaultLanguage())}
            colorScheme="brand"
            variant="link"
            size={{ base: 'sm', md: 'md' }}
            leftIcon={<Icon as={LuPlus} fontSize={20} color="brand.500" />}
          >
            {t('common:actions.addNew')}
          </Button>
        </Flex>
        {fields.map((field, idx) => (
          <Box
            key={field.id}
            borderBottom={idx === fields.length - 1 ? 'none' : '1px solid'}
            borderColor="gray.50"
            pb={4}
          >
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
              <FormField
                control={form.control}
                name={`languages.${idx}.language`}
                label={t('common:account.fields.foreignLanguage.label')}
                placeholder={t(
                  'common:account.fields.foreignLanguage.placeholder'
                )}
                type="text"
                size={{ base: 'sm', md: 'md' }}
              />
              <FormField
                control={form.control}
                name={`languages.${idx}.level`}
                label={t('common:account.fields.level.label')}
                placeholder={t('common:account.fields.level.placeholder')}
                type="select"
                options={languageLevels}
                size={{ base: 'sm', md: 'md' }}
              />
            </Grid>
            {fields.length > 1 && (
              <Button
                mt={2}
                variant="link"
                color="red.500"
                leftIcon={<Icon as={LuTrash2} fontSize={20} color="red.500" />}
                onClick={() => remove(idx)}
              >
                {t('common:actions.delete')}
              </Button>
            )}
          </Box>
        ))}
      </VStack>
    </Form>
  );
});

SkillInfoForm.displayName = 'SkillInfoForm';
